/**
 * Bostadshyresavtal — Version 2026 (Formulär R-01).
 *
 * One block per legacy `§` section, in the legacy order, plus the five
 * unnumbered static clauses. Slot keys and option values/codes are the
 * legacy template's, so one `values` bag drives both lanes. Clause text is
 * verbatim from Haymanot's approved Word template.
 */
import { group, nodes } from '@hyranai/regta-document-ir';
import { annexOrRule, referencedAttachments } from '../common/attachments.js';
import { customSectionsBlockDefinition } from '../common/custom-sections.js';
import { partyDetailsBlock, referenceLine } from '../common/fields.js';
import { signingBlockDefinition } from '../common/signing.js';
import { dateVal, numVal, optionChosen, optionVal, propertySlot, selectSlot, shown, slotText, toSlotOptions, textVal, whenOption, } from '../common/slots.js';
import { box, optionOf, rows, sectionBlock, unnumberedClause } from './kit.js';
import { APARTMENT_TYPE_OPTIONS, APARTMENT_USAGE_OPTIONS, COLD_WATER_OPTIONS, DEPOSIT_OPTIONS, ELECTRICITY_OPTIONS, HEATING_OPTIONS, HOT_WATER_OPTIONS, LEASE_TYPE_OPTIONS, MEMBER_FEE_OPTIONS, MEMBERSHIP_OPTIONS, NOTICE_TYPE_OPTIONS, PAYMENT_TYPE_OPTIONS, RENT_MODEL_OPTIONS, RENT_TYPE_OPTIONS, SPARE_KEY_OPTIONS, TENANT_ORG_OPTIONS, } from './options.js';
const REVIEW = { reviewedBy: 'Haymanot', source: 'Bostadshyresavtal – Version 2026 (Haymanot-approved Word template)' };
const K = 'bostadshyra';
/** "Avtalsnummer: … Objektsnummer: …" above the preamble. */
export const avtalsuppgifter = {
    blockKey: `${K}.avtalsuppgifter`,
    version: 'v1',
    plane: 'shared',
    status: 'published',
    language: 'sv-SE',
    title: 'Avtalsuppgifter',
    summary: 'The contract and object reference numbers printed above the preamble.',
    slots: [
        { key: 'contract_number', label: 'Avtalsnummer', type: 'text', required: true, help: 'Egen referens på avtalet, t.ex. A-2042.' },
        { key: 'object_number', label: 'Objektsnummer', type: 'text', help: 'Valfritt — egen referens på hyresobjektet.' },
    ],
    compile: (input, ctx) => nodes(referenceLine([
        ['Avtalsnummer', textVal(input, 'contract_number')],
        shown(ctx, slotText(input.object_number) != null) && ['Objektsnummer', textVal(input, 'object_number')],
    ])),
};
export const hyresvard = partyDetailsBlock({
    blockKey: `${K}.hyresvard`,
    variant: 'inline',
    ref: 'hyresvard',
    title: 'Hyresvärd',
    summary: 'The landlord: name, id number, address, e-mail and phone.',
    legalReview: REVIEW,
    groups: [
        {
            role: 'lessor',
            ordinal: 0,
            fields: [
                { key: 'landlord_name', label: 'Namn', field: 'name', required: true },
                { key: 'landlord_org_number', label: 'Personnr / Org.nr', field: 'legalId' },
                { key: 'landlord_address', label: 'Adress', field: 'address' },
                { key: 'landlord_email', label: 'E-post', field: 'email' },
                { key: 'landlord_phone', label: 'Telefon', field: 'phone' },
            ],
        },
    ],
});
export const hyresgast = partyDetailsBlock({
    blockKey: `${K}.hyresgast`,
    variant: 'inline',
    ref: 'hyresgast',
    title: 'Hyresgäst',
    summary: 'The tenant and, when present, the co-tenant: name, id number, e-mail and phone.',
    legalReview: REVIEW,
    groups: [
        {
            role: 'lessee',
            ordinal: 0,
            fields: [
                { key: 'tenant_name', label: 'Namn', field: 'name', required: true },
                { key: 'tenant_personnummer', label: 'Personnr / Org.nr', field: 'legalId', help: 'PII — ej sökbart' },
                { key: 'tenant_email', label: 'E-post', field: 'email' },
                { key: 'tenant_phone', label: 'Telefon', field: 'phone' },
            ],
        },
        {
            role: 'lessee',
            ordinal: 1,
            subheading: 'Medhyresgäst',
            showWhenAnyOf: ['cotenant_name', 'cotenant_personnummer', 'cotenant_email', 'cotenant_phone'],
            fields: [
                { key: 'cotenant_name', label: 'Namn (medhyresgäst)', field: 'name' },
                { key: 'cotenant_personnummer', label: 'Personnr / Org.nr (medhyresgäst)', field: 'legalId', help: 'PII — ej sökbart' },
                { key: 'cotenant_email', label: 'E-post (medhyresgäst)', field: 'email' },
                { key: 'cotenant_phone', label: 'Telefon (medhyresgäst)', field: 'phone' },
            ],
        },
    ],
});
export const lagenhetensAdress = sectionBlock({
    blockKey: `${K}.lagenhets-adress`,
    ref: 'lagenhets-adress',
    title: 'Lägenhetens adress',
    summary: 'Where the apartment is: municipality, property designation, floor, postal address and apartment numbers.',
    legalReview: REVIEW,
    slots: [
        propertySlot('property_municipality', 'Kommun', 'municipality', { required: true }),
        propertySlot('property_designation', 'Fastighetsbeteckning', 'designation', { required: true }),
        { key: 'property_staircase', label: 'Trappor', type: 'text' },
        {
            key: 'property_official_apt_no',
            label: 'Officiellt lägenhetsnummer',
            type: 'text',
            help: 'Enligt lägenhetsregister (Lantmäteriet). Fyrsiffrigt nummer, t.ex. 1201, 1508, 0903.',
        },
        propertySlot('property_address', 'Postadress', 'address', { type: 'textarea', required: true, help: 'Gatuadress' }),
        {
            key: 'property_landlord_apt_no',
            label: 'Hyresvärdens egna lägenhetsnummer',
            type: 'text',
            help: 'Valfritt. Hyresvärdens interna beteckning (t.ex. A1003, Lgh 14). Visas i avtalet endast om det fylls i.',
        },
    ],
    body: (input, ctx) => rows([
        ['Kommun', [textVal(input, 'property_municipality')]],
        ['Fastighetsbeteckning', [textVal(input, 'property_designation')]],
        ['Trappor', [textVal(input, 'property_staircase')]],
        ['Postadress', [textVal(input, 'property_address')]],
        ['Officiellt lägenhetsnummer', [textVal(input, 'property_official_apt_no')]],
        shown(ctx, slotText(input.property_landlord_apt_no) != null) && [
            'Hyresvärdens lägenhetsnummer',
            [textVal(input, 'property_landlord_apt_no')],
        ],
    ]),
});
export const anvandning = sectionBlock({
    blockKey: `${K}.anvandning`,
    ref: 'anvandning',
    title: 'Lägenhetens användning',
    summary: 'What the apartment may be used for (permanent home, corporate housing, …, or other).',
    legalReview: REVIEW,
    slots: [
        selectSlot('apartment_usage', 'Lägenhetens användning', APARTMENT_USAGE_OPTIONS, { required: true, default: 'stadigvarande' }),
        {
            key: 'apartment_usage_other',
            label: 'Annat ändamål (specificera)',
            type: 'text',
            help: 'Beskriv ändamålet',
            visibleWhen: whenOption('apartment_usage', optionOf(APARTMENT_USAGE_OPTIONS, 'annat')),
        },
    ],
    body: (input, ctx) => rows([
        ['Användning', [optionVal(APARTMENT_USAGE_OPTIONS, input, 'apartment_usage')]],
        shown(ctx, optionChosen(APARTMENT_USAGE_OPTIONS, input.apartment_usage, 'annat')) && [
            'Annat ändamål',
            [textVal(input, 'apartment_usage_other')],
        ],
    ]),
});
export const storlek = sectionBlock({
    blockKey: `${K}.storlek`,
    ref: 'storlek',
    title: 'Lägenhetens storlek',
    summary: 'Apartment type (rooms and kitchen) and area in square metres.',
    legalReview: REVIEW,
    slots: [
        selectSlot('apartment_type', 'Lägenhetstyp', APARTMENT_TYPE_OPTIONS, { required: true }),
        { key: 'apartment_area', label: 'Area i m²', type: 'number', required: true },
    ],
    body: (input, ctx) => rows([
        ['Lägenhetstyp', [optionVal(APARTMENT_TYPE_OPTIONS, input, 'apartment_type')]],
        ['Area (m²)', [numVal(ctx, input, 'apartment_area')]],
    ]),
});
export const tillbehor = sectionBlock({
    blockKey: `${K}.tillbehor`,
    ref: 'tillbehor',
    title: 'Till lägenheten hör',
    summary: 'Whether an attic and/or basement storage space comes with the apartment, and their numbers.',
    legalReview: REVIEW,
    slots: [
        { key: 'has_attic', label: 'Vindsutrymme ingår', type: 'boolean' },
        { key: 'attic_number', label: 'Vindsutrymme nr', type: 'text', visibleWhen: { key: 'has_attic', equals: true } },
        { key: 'has_basement', label: 'Källarutrymme ingår', type: 'boolean' },
        { key: 'basement_number', label: 'Källarutrymme nr', type: 'text', visibleWhen: { key: 'has_basement', equals: true } },
    ],
    body: (input, ctx) => [
        group([
            group(rows([
                ['Vindsutrymme ingår', [box(ctx, input.has_attic === true, 'has_attic')]],
                shown(ctx, input.has_attic === true) && ['Vindsutrymme nr', [textVal(input, 'attic_number')]],
            ])),
            group(rows([
                ['Källarutrymme ingår', [box(ctx, input.has_basement === true, 'has_basement')]],
                shown(ctx, input.has_basement === true) && ['Källarutrymme nr', [textVal(input, 'basement_number')]],
            ])),
        ], { variant: 'columns' }),
    ],
});
const isFixedTerm = (input) => optionChosen(LEASE_TYPE_OPTIONS, input.lease_type, 'bestamd_tid');
export const hyrestid = sectionBlock({
    blockKey: `${K}.hyrestid`,
    ref: 'hyrestid',
    title: 'Hyrestid',
    summary: 'Open-ended or fixed-term lease, with start and (for a fixed term) end date.',
    legalReview: REVIEW,
    slots: [
        selectSlot('lease_type', 'Hyresform', LEASE_TYPE_OPTIONS, { required: true, default: 'tillsvidare' }),
        { key: 'lease_start_date', label: 'Startdatum', type: 'date', required: true },
        {
            key: 'lease_end_date',
            label: 'Slutdatum (vid bestämd tid)',
            type: 'date',
            visibleWhen: whenOption('lease_type', optionOf(LEASE_TYPE_OPTIONS, 'bestamd_tid')),
        },
    ],
    body: (input, ctx) => rows([
        ['Hyresform', [optionVal(LEASE_TYPE_OPTIONS, input, 'lease_type')]],
        ['Startdatum', [dateVal(ctx, input, 'lease_start_date')]],
        shown(ctx, isFixedTerm(input)) && ['Slutdatum', [dateVal(ctx, input, 'lease_end_date')]],
    ]),
});
export const uppsagning = sectionBlock({
    blockKey: `${K}.uppsagning`,
    ref: 'uppsagning',
    title: 'Uppsägning',
    summary: 'Notice period (three months or a custom number) and, for a fixed term, the extension period.',
    legalReview: REVIEW,
    slots: [
        selectSlot('notice_type', 'Uppsägningstyp', NOTICE_TYPE_OPTIONS, { required: true, default: 'standard_3m' }),
        {
            key: 'notice_months',
            label: 'Antal månader',
            type: 'number',
            visibleWhen: whenOption('notice_type', optionOf(NOTICE_TYPE_OPTIONS, 'annat')),
        },
        {
            key: 'extension_months',
            label: 'Förlängning (månader)',
            type: 'number',
            visibleWhen: whenOption('lease_type', optionOf(LEASE_TYPE_OPTIONS, 'bestamd_tid')),
        },
    ],
    body: (input, ctx) => rows([
        ['Uppsägningstyp', [optionVal(NOTICE_TYPE_OPTIONS, input, 'notice_type')]],
        shown(ctx, optionChosen(NOTICE_TYPE_OPTIONS, input.notice_type, 'annat')) && [
            'Antal månader',
            [numVal(ctx, input, 'notice_months')],
        ],
        shown(ctx, isFixedTerm(input)) && ['Förlängning (månader)', [numVal(ctx, input, 'extension_months')]],
    ]),
});
const isTidsbestamt = (input) => optionChosen(RENT_MODEL_OPTIONS, input.rent_model, 'tidsbestamt');
export const isKollektiv = (input) => optionChosen(RENT_MODEL_OPTIONS, input.rent_model, 'kollektiv');
export const hyressattningsmodell = sectionBlock({
    blockKey: `${K}.hyressattningsmodell`,
    ref: 'hyressattningsmodell',
    title: 'Hyressättningsmodell',
    summary: 'How the rent is set: collective negotiation, a time-limited rent-setting agreement (annexed), or direct negotiation.',
    legalReview: REVIEW,
    slots: [
        selectSlot('rent_model', 'Hyressättningsmodell', RENT_MODEL_OPTIONS, { required: true }),
        {
            key: 'rent_model_annex',
            label: 'Bilaga (hyressättningsavtal)',
            type: 'attachment',
            help: 'Välj uppladdad bilaga med det tidsbestämda hyressättningsavtalet.',
            visibleWhen: whenOption('rent_model', optionOf(RENT_MODEL_OPTIONS, 'tidsbestamt')),
        },
    ],
    body: (input, ctx) => rows([
        ['Modell', [optionVal(RENT_MODEL_OPTIONS, input, 'rent_model')]],
        shown(ctx, isTidsbestamt(input)) && ['Bilaga (hyressättningsavtal)', annexOrRule(ctx, input.rent_model_annex, 'rent_model_annex', 'medium')],
    ]),
    attachments: (input, ctx) => (isTidsbestamt(input) ? referencedAttachments(ctx, input.rent_model_annex) : []),
});
export const forhandlingsklausul = sectionBlock({
    blockKey: `${K}.forhandlingsklausul`,
    ref: 'forhandlingsklausul',
    title: 'Förhandlingsklausul',
    summary: 'Whether a negotiation clause applies and with which tenant organisation. Only present under collective negotiation.',
    legalReview: REVIEW,
    slots: [
        {
            key: 'negotiation_clause',
            label: 'Förhandlingsklausul gäller',
            type: 'boolean',
            default: true,
            visibleWhen: whenOption('rent_model', optionOf(RENT_MODEL_OPTIONS, 'kollektiv')),
        },
        {
            key: 'tenant_org',
            label: 'Hyresgästorganisation',
            type: 'select_with_freetext',
            options: toSlotOptions(TENANT_ORG_OPTIONS),
            visibleWhen: whenOption('rent_model', optionOf(RENT_MODEL_OPTIONS, 'kollektiv')),
        },
    ],
    body: (input, ctx) => shown(ctx, isKollektiv(input))
        ? rows([
            ['Förhandlingsklausul gäller', [box(ctx, input.negotiation_clause === true, 'negotiation_clause')]],
            ['Hyresgästorganisation', [optionVal(TENANT_ORG_OPTIONS, input, 'tenant_org')]],
        ])
        : null,
});
export const hyra = sectionBlock({
    blockKey: `${K}.hyra`,
    ref: 'hyra',
    title: 'Hyra',
    summary: 'Monthly rent, rent type and whether the tenant-union fee and membership are included.',
    legalReview: REVIEW,
    slots: [
        { key: 'rent_amount', label: 'Hyra (kr/mån)', type: 'currency', required: true },
        selectSlot('rent_includes_member_fee', 'Avgift till hyresgästorg. ingår', MEMBER_FEE_OPTIONS),
        selectSlot('rent_includes_membership', 'Medlemskap i hyresgästorg. ingår', MEMBERSHIP_OPTIONS),
        selectSlot('rent_type', 'Hyrestyp', RENT_TYPE_OPTIONS, { required: true }),
    ],
    body: (input, ctx) => rows([
        ['Hyra (kr/mån)', [numVal(ctx, input, 'rent_amount')]],
        ['Hyrestyp', [optionVal(RENT_TYPE_OPTIONS, input, 'rent_type')]],
        ['Avgift till hyresgästorg. ingår', [optionVal(MEMBER_FEE_OPTIONS, input, 'rent_includes_member_fee')]],
        ['Medlemskap i hyresgästorg. ingår', [optionVal(MEMBERSHIP_OPTIONS, input, 'rent_includes_membership')]],
    ]),
});
export const tillagg = sectionBlock({
    blockKey: `${K}.tillagg`,
    ref: 'tillagg',
    title: 'Tillägg',
    summary: 'Heating, cold/hot water and household electricity (included or individually metered) and optional equipment (annexed).',
    legalReview: REVIEW,
    slots: [
        selectSlot('heating_included', 'Uppvärmning', HEATING_OPTIONS, { required: true }),
        selectSlot('cold_water_included', 'Kallvatten', COLD_WATER_OPTIONS, { required: true }),
        selectSlot('hot_water_included', 'Varmvatten', HOT_WATER_OPTIONS, { required: true }),
        selectSlot('electricity_included', 'Hushållsel', ELECTRICITY_OPTIONS, { required: true }),
        { key: 'extras_included', label: 'Tillval av utrustning', type: 'boolean' },
        {
            key: 'extras_annex',
            label: 'Bilaga (tillvalsavtal)',
            type: 'attachment',
            help: 'Välj uppladdad bilaga med tillvalsavtalet.',
            visibleWhen: { key: 'extras_included', equals: true },
        },
    ],
    body: (input, ctx) => rows([
        ['Uppvärmning', [optionVal(HEATING_OPTIONS, input, 'heating_included')]],
        ['Kallvatten', [optionVal(COLD_WATER_OPTIONS, input, 'cold_water_included')]],
        ['Varmvatten', [optionVal(HOT_WATER_OPTIONS, input, 'hot_water_included')]],
        ['Hushållsel', [optionVal(ELECTRICITY_OPTIONS, input, 'electricity_included')]],
        ['Tillval av utrustning', [box(ctx, input.extras_included === true, 'extras_included')]],
        shown(ctx, input.extras_included === true) && ['Bilaga (tillvalsavtal)', annexOrRule(ctx, input.extras_annex, 'extras_annex', 'medium')],
    ]),
    attachments: (input, ctx) => (input.extras_included === true ? referencedAttachments(ctx, input.extras_annex) : []),
});
const paysToAccount = (input) => optionChosen(PAYMENT_TYPE_OPTIONS, input.payment_type, 'plusgiro') || optionChosen(PAYMENT_TYPE_OPTIONS, input.payment_type, 'bankgiro');
export const betalning = sectionBlock({
    blockKey: `${K}.betalning`,
    ref: 'betalning',
    title: 'Hyrans betalning',
    summary: 'How the rent is paid (invoice, plusgiro or bankgiro) and the account number.',
    legalReview: REVIEW,
    slots: [
        selectSlot('payment_type', 'Betalningssätt', PAYMENT_TYPE_OPTIONS, { required: true }),
        {
            key: 'payment_account',
            label: 'Kontonummer',
            type: 'text',
            visibleWhen: whenOption('payment_type', optionOf(PAYMENT_TYPE_OPTIONS, 'plusgiro'), optionOf(PAYMENT_TYPE_OPTIONS, 'bankgiro')),
        },
    ],
    body: (input, ctx) => rows([
        ['Betalningssätt', [optionVal(PAYMENT_TYPE_OPTIONS, input, 'payment_type')]],
        shown(ctx, paysToAccount(input)) && ['Kontonummer', [textVal(input, 'payment_account')]],
    ]),
});
const depositRequired = (input) => optionChosen(DEPOSIT_OPTIONS, input.deposit_required, 'ja');
export const deposition = sectionBlock({
    blockKey: `${K}.deposition`,
    ref: 'deposition',
    title: 'Deposition',
    summary: 'Whether a deposit is required, its size in monthly rents and the repayment period in days.',
    legalReview: REVIEW,
    slots: [
        selectSlot('deposit_required', 'Deposition krävs', DEPOSIT_OPTIONS, { default: 'nej' }),
        { key: 'deposit_months', label: 'Depositionsbelopp (månadshyror)', type: 'number', visibleWhen: whenOption('deposit_required', optionOf(DEPOSIT_OPTIONS, 'ja')) },
        { key: 'deposit_return_days', label: 'Återbetalning inom (dagar)', type: 'number', default: 30, visibleWhen: whenOption('deposit_required', optionOf(DEPOSIT_OPTIONS, 'ja')) },
    ],
    body: (input, ctx) => rows([
        ['Deposition krävs', [optionVal(DEPOSIT_OPTIONS, input, 'deposit_required')]],
        shown(ctx, depositRequired(input)) && ['Depositionsbelopp (månadshyror)', [numVal(ctx, input, 'deposit_months')]],
        shown(ctx, depositRequired(input)) && ['Återbetalning inom (dagar)', [numVal(ctx, input, 'deposit_return_days')]],
    ]),
});
export const garage = sectionBlock({
    blockKey: `${K}.garage`,
    ref: 'garage',
    title: 'Garage / Bilplats',
    summary: 'Garage or parking space: by separate agreement, fee included in the rent, and the space number.',
    legalReview: REVIEW,
    slots: [
        { key: 'garage_separate_agreement', label: 'enligt särskilt avtal', type: 'boolean' },
        { key: 'garage_included_in_rent', label: 'Avgift ingår i hyran', type: 'boolean' },
        { key: 'garage_spot_number', label: 'Plats nr', type: 'text' },
    ],
    body: (input, ctx) => rows([
        ['enligt särskilt avtal', [box(ctx, input.garage_separate_agreement === true, 'garage_separate_agreement')]],
        ['Avgift ingår i hyran', [box(ctx, input.garage_included_in_rent === true, 'garage_included_in_rent')]],
        ['Plats nr', [textVal(input, 'garage_spot_number')]],
    ]),
});
export const reservnycklar = sectionBlock({
    blockKey: `${K}.reservnycklar`,
    ref: 'reservnycklar',
    title: 'Reservnycklar',
    summary: 'Whether the landlord may keep a spare key.',
    legalReview: REVIEW,
    slots: [
        selectSlot('spare_key_consent', 'Förvaring av reservnyckel hos hyresvärden', SPARE_KEY_OPTIONS, { required: true }),
    ],
    body: (input) => rows([['Förvaring av reservnyckel hos hyresvärden', [optionVal(SPARE_KEY_OPTIONS, input, 'spare_key_consent')]]]),
});
export const sarskildaBestammelser = sectionBlock({
    blockKey: `${K}.sarskilda-bestammelser`,
    ref: 'sarskilda-bestammelser',
    title: 'Särskilda bestämmelser',
    summary: 'Whether the smoking ban applies, and any other special conditions.',
    legalReview: REVIEW,
    slots: [
        { key: 'no_smoking', label: 'Rökförbud gäller', type: 'boolean', default: true },
        { key: 'special_conditions', label: 'Särskilda bestämmelser', type: 'textarea', help: 'Ange eventuella särskilda villkor här' },
    ],
    body: (input, ctx) => rows([
        ['Rökförbud gäller', [box(ctx, input.no_smoking === true, 'no_smoking')]],
        shown(ctx, slotText(input.special_conditions) != null) && ['Övrigt', [textVal(input, 'special_conditions', 'long')]],
    ]),
});
export const hinderOchMen = unnumberedClause({
    blockKey: `${K}.hinder-och-men`,
    ref: 'hinder-och-men',
    title: 'Hinder och men i nyttjanderätten',
    summary: 'Rent reduction when the tenant’s use is impeded through no fault of theirs; not during ordinary maintenance.',
    legalReview: REVIEW,
    body: [
        'Om hinder eller men uppstår i nyttjanderätten utan hyresgästens vållande har hyresgästen rätt till skälig nedsättning av hyran för den tid som lägenheten är i bristfälligt skick. Hyresgästen har inte rätt till nedsättning av hyran under tiden då hyresvärden utför sedvanligt underhåll i lägenheten eller fastigheten under förutsättning av att arbetet utförs utan onödig tidsutdräkt.',
    ],
});
export const hyresgastensAtaganden = unnumberedClause({
    blockKey: `${K}.hyresgastens-ataganden`,
    ref: 'hyresgastens-ataganden',
    title: 'Hyresgästens åtaganden',
    summary: 'The tenant’s undertakings: duty of care, no subletting without consent, occupancy, waste, snow, access, viewings, move-out.',
    legalReview: REVIEW,
    body: [
        'Hyresgästen åtar sig:',
        {
            bullets: [
                'att iaktta sin vårdplikt i sin användning av lägenheten och vad som hör till den samt vid användning av de gemensamma utrymmena.',
                'att inte utan hyresvärdens samtycke eller hyresnämndens tillstånd upplåta lägenheten i andra hand.',
                'att inte låta fler personer stadigvarande bo i lägenheten än vad den är avsedd för.',
                'att på egen bekostnad forsla bort avfall som är olämpligt för soptunna eller sopnedkast.',
                'att hålla till lägenheten hörande balkong eller uteplats fri från snö och is.',
                'att lämna hyresvärden tillträde till lägenheten för tillsyn eller förbättringsarbete.',
                'att när lägenheten sagts upp, låta den visas för ny hyresgäst på lämplig tid.',
                'att vid flytt överlämna samtliga port- och dörrnycklar samt städa lägenheten noggrant.',
            ],
        },
    ],
});
const rokforbudClause = unnumberedClause({
    blockKey: `${K}.rokforbud`,
    ref: 'rokforbud',
    title: 'Rökförbud',
    summary: 'Smoking is not allowed in the apartment or the building’s common areas. Present only when the smoking ban is ticked.',
    legalReview: REVIEW,
    body: ['Rökning är inte tillåten i lägenheten eller husets allmänna och gemensamma utrymmen.'],
});
export const rokforbud = {
    ...rokforbudClause,
    slots: [{ key: 'no_smoking', label: 'Rökförbud gäller', type: 'boolean' }],
    compile: (input, ctx) => (shown(ctx, input.no_smoking === true) ? rokforbudClause.compile(input, ctx) : null),
};
export const forceMajeure = unnumberedClause({
    blockKey: `${K}.force-majeure`,
    ref: 'force-majeure',
    title: 'Force Majeure',
    summary: 'The landlord is excused for failures caused by war, riot, strikes, fire, explosion or public authority beyond its control.',
    legalReview: REVIEW,
    body: [
        'Hyresvärden fritar sig från skyldighet att fullgöra sin del av avtalet och från skyldighet att betala skadestånd om åtagandena inte alls eller endast till onormalt hög kostnad kan fullgöras på grund av krig, upplopp, arbetsinställelse, blockad, eldsvåda, explosion eller ingrepp av offentlig myndighet som hyresvärden inte råder över och inte heller kunnat förutse.',
    ],
});
export const tidigareHyresavtal = unnumberedClause({
    blockKey: `${K}.tidigare-hyresavtal`,
    ref: 'tidigare-hyresavtal',
    title: 'Tidigare hyresavtal',
    summary: 'Any earlier lease between the parties for this apartment ends when this one takes effect.',
    legalReview: REVIEW,
    body: [
        'Eventuellt tidigare hyresavtal mellan parterna avseende denna lägenhet upphör att gälla fr.o.m. dagen för detta avtals ikraftträdande.',
    ],
});
const GUARANTOR_SLOT = {
    key: 'has_guarantor',
    label: 'Avtalet har borgensman',
    type: 'boolean',
    default: false,
    help: 'Bocka i om någon ska gå i borgen för hyran. Lägg sedan till borgensmannen i partilistan ovan.',
};
export const underskrifter = signingBlockDefinition({
    blockKey: `${K}.underskrifter`,
    summary: 'Signature panels for landlord, tenant, co-tenant (when named) and guarantor (when ticked), with the execution statement.',
    defaults: { ref: 'underskrift', title: 'Underskrifter' },
    slots: [GUARANTOR_SLOT],
});
export const customSections = customSectionsBlockDefinition(`${K}.custom-sections`);
export const BOSTADSHYRA_BLOCKS = [
    avtalsuppgifter,
    hyresvard,
    hyresgast,
    lagenhetensAdress,
    anvandning,
    storlek,
    tillbehor,
    hyrestid,
    uppsagning,
    hyressattningsmodell,
    forhandlingsklausul,
    hyra,
    tillagg,
    betalning,
    deposition,
    garage,
    reservnycklar,
    sarskildaBestammelser,
    hinderOchMen,
    hyresgastensAtaganden,
    rokforbud,
    forceMajeure,
    tidigareHyresavtal,
    underskrifter,
    customSections,
];
//# sourceMappingURL=blocks.js.map